import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  TextField,
  InputLabel,
  FormControl,
  Select,
  Autocomplete,
  IconButton,
  CircularProgress
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heIL } from '@mui/x-data-grid/locales';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { format } from 'date-fns';
import API from '../services/api';
import MobileDialog from './dialogs/MobileDialog';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../contexts/UserContext';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// ערכת נושא בעברית ומימין לשמאל
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
});

const FunctionalGroupsPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  // Get UI configuration from user profile
  const uiConfig = userProfile?.ui_config || {};
  const includeGroups = uiConfig.include_groups !== false; // Default to true if not specified
  const allowedGroupTypes = uiConfig.group_types || ['Flow', 'Year', 'General']; // Default if not specified
  const groupsSchemas = uiConfig.groups_schemas || {};
  const debtFields = uiConfig.debt_fields?.filter(field => field !== 'TotalDebt') || [];
  const debtFieldsSchema = uiConfig.debt_fields_schema || {};

  // Helper function to get group type label from schema
  const getGroupTypeLabel = (groupType) => {
    return groupsSchemas[groupType]?.label || 
           (groupType === 'Flow' ? 'שוטף' : 
            groupType === 'Year' ? 'שנתי' : 
            groupType === 'General' ? 'כללי' : 
            groupType);
  };

  // רשימת קבוצות
  const [groups, setGroups] = useState([]);
  // חיפוש וסינון
  const [searchTerm, setSearchTerm] = useState('');
  const [groupTypeFilter, setGroupTypeFilter] = useState('All');

  // דיאלוג עדכון חוב
  const [actionDialogOpen, setActionDialogOpen] = useState(false);

  // שלבי הדיאלוג: 'form' -> 'confirm' -> 'password'
  const [actionFormStep, setActionFormStep] = useState('form');

  const [currentGroup, setCurrentGroup] = useState(null);

  // שדות בטופס
  const [updateAmount, setUpdateAmount] = useState('');
  const [debtField, setDebtField] = useState('');
  const [notes, setNotes] = useState('');
  const [actualDate, setActualDate] = useState(null);

  // ==== password step ====
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  // ==== loading & messages in final step ====
  const [finalLoading, setFinalLoading] = useState(false);
  const [finalError, setFinalError] = useState('');
  const [finalSuccess, setFinalSuccess] = useState('');

  // שגיאה מקומית לטופס (צעד 1/2)
  const [localError, setLocalError] = useState('');
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if ((localError || finalError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [localError, finalError]);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // שליפה ראשונית של כל הקבוצות
  useEffect(() => {
    if (!includeGroups) {
      navigate('/');
      return;
    }
    
    fetchGroups();
  }, [includeGroups, navigate]);

  const fetchGroups = async () => {
    try {
      const res = await API.get('/groups/');
      setGroups(res.data);
    } catch (error) {
      console.error('שגיאה בטעינת הקבוצות:', error);
      showSnackbar('שגיאה בטעינת הקבוצות', 'error');
    }
  };

  // הצעות להשלמה אוטומטית (שמות קבוצות)
  const groupNameOptions = useMemo(() => {
    const uniqueNames = new Set(groups.map((g) => g.GroupName.trim()));
    return Array.from(uniqueNames);
  }, [groups]);

  // סינון לפי חיפוש וסוג קבוצה
  const filteredGroups = useMemo(() => {
    let filtered = [...groups];
    if (searchTerm.trim()) {
      filtered = filtered.filter((g) =>
        g.GroupName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (groupTypeFilter !== 'All') {
      filtered = filtered.filter((g) => g.GroupType === groupTypeFilter);
    }
    return filtered;
  }, [groups, searchTerm, groupTypeFilter]);

  // פתיחת הדיאלוג
  const handleOpenDialog = (group) => {
    setCurrentGroup(group);
    setUpdateAmount('');

    // Set default debt field based on group type and config
    if (group.GroupType === 'General') {
      setDebtField(debtFields.length > 0 ? debtFields[0] : '');
    } else {
      // For non-general groups, try to find the target debt field from schema
      const targetField = groupsSchemas[group.GroupType]?.target_debt_field || '';
      setDebtField(targetField);
    }

    setNotes('');
    setActualDate(new Date());
    setLocalError('');
    setPassword('');
    setPasswordVisible(false);
    setFinalLoading(false);
    setFinalError('');
    setFinalSuccess('');
    setActionFormStep('form');
    setActionDialogOpen(true);
  };

  // סגירת הדיאלוג
  const handleCloseDialog = () => {
    setActionDialogOpen(false);
    setCurrentGroup(null);
    setActionFormStep('form');
    setActualDate(null);
    setPassword('');
    setPasswordVisible(false);
    setFinalLoading(false);
    setFinalError('');
    setFinalSuccess('');
  };

  // מעבר משלב טופס -> אישור
  const handleFormNextStep = () => {
    if (!updateAmount || parseFloat(updateAmount) === 0) {
      setLocalError('אנא הזן סכום חוקי (אינו יכול להיות אפס).');
      return;
    }
    if (currentGroup.GroupType === 'General' && !debtField) {
      setLocalError('אנא בחר שדה חוב עבור קבוצה כללית.');
      return;
    }
    setLocalError('');
    setActionFormStep('confirm');
  };

  // אישור -> סיסמה
  const handleConfirmNextStep = () => {
    setLocalError('');
    setActionFormStep('password');
  };
  
  const queryClient = useQueryClient();

  const invalidateClientListCache = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['clients'],
      refetchType: 'active', // Ensures active queries refetch immediately
    });
  };

  // שליחת העדכון (שלב סופי)
  const handleSubmitAction = async () => {
    if (finalLoading) return;
    setFinalLoading(true);
    setFinalError('');
    setFinalSuccess('');

    try {
      const payload = {
        group_id: currentGroup.GroupID,
        amount: parseFloat(updateAmount),
        notes: notes || undefined,
      };
      if (currentGroup.GroupType === 'General') {
        payload.debt_field = debtField;
      }
      // Add actual_date if provided (format as ISO string for backend)
      if (actualDate) {
        payload.actual_date = actualDate.toISOString();
      }
      payload.password = password; // password protection

      await API.post('/group/action-submit/', payload);

      // SUCCESS
      setFinalSuccess('העדכון בוצע בהצלחה!');
      await invalidateClientListCache();

      fetchGroups(); // רענון הרשימה

      // Auto-close the dialog after success (shorter time now)
      setTimeout(() => {
        handleCloseDialog();
      }, 1000);
    } catch (error) {
      console.error('שגיאה בעדכון החוב:', error);

      if (error.response && error.response.status === 401) {
        // password invalid
        setFinalError('סיסמה שגויה');
      } else {
        // other server error
        setFinalError('שגיאה בעדכון הקבוצה');
      }
    } finally {
      setFinalLoading(false);
    }
  };

  // Snackbar עזר
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  if (!includeGroups) {
    return null; // Don't render if groups are not included in config
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <ThemeProvider theme={theme}>
        <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
          <Typography variant="h3" sx={{ mb: 3 }}>
            פעילות קבוצתית
          </Typography>

          <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 2 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                  רשימת קבוצות
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 2,
              }}
            />
            <CardContent sx={{ fontSize: '1.2rem' }}>
              {/* חיפוש + סינון */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Autocomplete
                  freeSolo
                  options={groupNameOptions}
                  inputValue={searchTerm}
                  onInputChange={(event, newValue) => setSearchTerm(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="חיפוש לפי שם קבוצה"
                      size="medium"
                      sx={{ width: 300 }}
                    />
                  )}
                />

                <TextField
                  label="סוג קבוצה"
                  select
                  size="medium"
                  value={groupTypeFilter}
                  onChange={(e) => setGroupTypeFilter(e.target.value)}
                  sx={{ width: 160 }}
                >
                  <MenuItem value="All">הכל</MenuItem>
                  {/* Use allowed group types from config */}
                  {allowedGroupTypes.map(groupType => (
                    <MenuItem key={groupType} value={groupType}>
                      {getGroupTypeLabel(groupType)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* רשימת קבוצות בגלילה */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                  gap: 2,
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                {filteredGroups.length === 0 && (
                  <Typography>לא נמצאו קבוצות תואמות</Typography>
                )}

                {filteredGroups.map((group) => (
                  <Card
                    key={group.GroupID}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      backgroundColor: '#f4f8ff',
                      borderRadius: 2,
                      boxShadow: 2,
                      p: 2,
                    }}
                  >
                    <Box sx={{ p: 0 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          mb: 1,
                          fontWeight: 'bold',
                        }}
                        onClick={() => navigate(`/group/${group.GroupID}`)}
                      >
                        {group.GroupName}
                      </Typography>

                      <Typography sx={{ mb: 1, fontSize: '1.1rem' }}>
                        {group.Description || ''}
                      </Typography>

                      <Typography sx={{ fontSize: '1rem' }} color="text.secondary">
                        סוג קבוצה: {getGroupTypeLabel(group.GroupType)}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right', mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{ fontSize: '1rem' }}
                        onClick={() => handleOpenDialog(group)}
                      >
                        עדכן חוב
                      </Button>
                    </Box>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* דיאלוג עדכון חוב */}
          <MobileDialog
            open={actionDialogOpen}
            onClose={(event, reason) => {
              if (reason !== 'backdropClick') {
                handleCloseDialog();
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              {actionFormStep === 'form'
                ? 'עדכון חוב'
                : actionFormStep === 'confirm'
                ? 'אישור נתונים'
                : 'הזן סיסמה לאישור סופי'}
            </DialogTitle>

            <DialogContent dividers sx={{ fontSize: '1.2rem' }}>
              {/* Step 1/2 error (localError) */}
              {localError && actionFormStep !== 'password' && (
                <Alert ref={errorRef} severity="error" sx={{ mb: 2 }}>
                  {localError}
                </Alert>
              )}

              {/* Step 3 error/success */}
              {actionFormStep === 'password' && (
                <>
                  {finalError && (
                    <Alert ref={errorRef} severity="error" sx={{ mb: 2 }}>
                      {finalError}
                    </Alert>
                  )}
                  {finalSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {finalSuccess}
                    </Alert>
                  )}
                </>
              )}

              {/* שלב טופס */}
              {actionFormStep === 'form' && currentGroup && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="info">
                    כאן ניתן לעדכן את סכום החוב עבור כל הלקוחות בקבוצה.
                    בקבוצה כללית יש לבחור גם את השדה הספציפי לעדכון.
                  </Alert>

                  <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                    קבוצה: {currentGroup.GroupName}
                  </Typography>
                  <Typography sx={{ fontSize: '1.1rem' }}>
                    סוג קבוצה: {getGroupTypeLabel(currentGroup.GroupType)}
                  </Typography>

                  <TextField
                    label="סכום לעדכון"
                    type="number"
                    value={updateAmount}
                    onChange={(e) => setUpdateAmount(e.target.value)}
                    fullWidth
                    sx={{
                      direction: 'ltr',
                      '& input': {
                        textAlign: 'right',
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '-moz-appearance': 'textfield',
                      },
                    }}
                  />

                  {currentGroup.GroupType === 'General' && (
                    <FormControl fullWidth>
                      <InputLabel id="debt-field-label">
                        בחר שדה חוב
                      </InputLabel>
                      <Select
                        labelId="debt-field-label"
                        label="בחר שדה חוב"
                        value={debtField}
                        onChange={(e) => setDebtField(e.target.value)}
                      >
                        {/* Use debt fields from config */}
                        {debtFields.map((field) => (
                          <MenuItem key={field} value={field}>
                            {debtFieldsSchema[field]?.label || field}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <DatePicker
                    label="תאריך בפועל"
                    value={actualDate}
                    onChange={(newValue) => setActualDate(newValue)}
                    format="dd/MM/yyyy"
                    sx={{direction: 'ltr'}}
                    slotProps={{
                      textField: { fullWidth: true },
                      actionBar: { actions: ['today', 'accept'] },
                    }}
                  />

                  {/* הערות (אופציונלי) */}
                  <TextField
                    label="הערות (אופציונלי)"
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                  />
                </Box>
              )}

              {/* שלב אישור */}
              {actionFormStep === 'confirm' && currentGroup && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    האם לאשר את העדכון הבא?
                  </Typography>
                  <Typography>שם הקבוצה: {currentGroup.GroupName}</Typography>
                  <Typography>
                    סוג קבוצה: {getGroupTypeLabel(currentGroup.GroupType)}
                  </Typography>
                  <Typography>סכום: {updateAmount}</Typography>

                  {currentGroup.GroupType === 'General' && debtField && (
                    <Typography>
                      שדה: {debtFieldsSchema[debtField]?.label || debtField}
                    </Typography>
                  )}

                  <Typography>
                    תאריך בפועל: {actualDate ? format(actualDate, 'dd/MM/yyyy') : 'היום'}
                  </Typography>

                  {notes && <Typography>הערות: {notes}</Typography>}
                </Box>
              )}

              {/* שלב סיסמה */}
              {actionFormStep === 'password' && !finalSuccess && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography>
                    כדי להשלים את העדכון, הזן/י את הסיסמה שלך:
                  </Typography>
                  <TextField
                    label="סיסמה"
                    type={passwordVisible ? 'text' : 'password'}
                    value={password}
                    sx={{ direction: 'ltr' }}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          edge="end"
                        >
                          {passwordVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              {/* Render cancel/back buttons only if in steps other than password OR if in password but no success message */}
              {!(actionFormStep === 'password' && finalSuccess) && (
                <Button onClick={handleCloseDialog} color="inherit" size="large" disabled={finalLoading}>
                  ביטול
                </Button>
              )}

              {actionFormStep === 'form' && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleFormNextStep}
                >
                  שלב הבא
                </Button>
              )}

              {actionFormStep === 'confirm' && (
                <>
                  <Button
                    color="inherit"
                    size="large"
                    onClick={() => setActionFormStep('form')}
                  >
                    חזרה
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleConfirmNextStep}
                  >
                    אישור
                  </Button>
                </>
              )}

              {actionFormStep === 'password' && !finalSuccess && (
                <>
                  <Button
                    color="inherit"
                    size="large"
                    onClick={() => {
                      setFinalError('');
                      setFinalSuccess('');
                      setActionFormStep('confirm');
                    }}
                    disabled={finalLoading}
                  >
                    חזרה
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmitAction}
                    disabled={finalLoading}
                  >
                    {finalLoading ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1, color: 'inherit' }} />
                        מעדכן...
                      </>
                    ) : (
                      'אישור סופי'
                    )}
                  </Button>
                </>
              )}
            </DialogActions>
          </MobileDialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
          </Container>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default FunctionalGroupsPage;